import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Spinner,
  Text,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  Button,
  useToast,
} from "@chakra-ui/react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import {
  FiUsers,
  FiTrendingUp,
  FiTrendingDown,
  FiActivity,
} from "react-icons/fi";
import { api_url } from "../../url";
import Navbar from "../../Components/Navbar/Navbar";
import Sidebar from "../../Components/Sidebar";

const StatCard = ({ label, value, icon, color, subtext }) => (
  <Box
    bg="white"
    p={6}
    borderRadius="lg"
    boxShadow="sm"
    border="1px solid"
    borderColor="gray.100"
  >
    <Flex justify="space-between" align="start">
      <Box>
        <Text fontSize="sm" color="gray.500" mb={1}>
          {label}
        </Text>
        <Text fontSize="2xl" fontWeight="bold" color="gray.700">
          {value}
        </Text>
        {subtext && (
          <Text
            fontSize="xs"
            color={subtext.includes("-") ? "red.500" : "green.500"}
          >
            {subtext}
          </Text>
        )}
      </Box>
      <Box p={3} bg={`${color}.50`} borderRadius="md">
        <Icon as={icon} boxSize={6} color={`${color}.500`} />
      </Box>
    </Flex>
  </Box>
);

const CasinoAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const toast = useToast();
  const SIDEBAR_WIDTH = "240px";

  const itemsPerPage = 10;

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const response = await axios.get(`${api_url}/analytics/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error(error);

      toast({
        title: "Error fetching analytics",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const { summary, daily, recentWins = [] } = data || {};

  const totalPages = Math.ceil(recentWins.length / itemsPerPage);

  const paginatedWins = recentWins.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  return (
    <>
      <Navbar />
      <Sidebar />

      <Box
        p={{ base: 4, md: 8 }}
        bg="gray.50"
        minH="100vh"
        mt="64px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        <Text fontSize="2xl" fontWeight="bold" mb={6} color="gray.800">
          Casino Analytics
        </Text>

        {/* Summary Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
          <StatCard
            label="Gross Gaming Revenue (GGR)"
            value={`₹${summary?.ggr?.toFixed(2) || "0.00"}`}
            icon={FiActivity}
            color="purple"
            subtext="Net Revenue"
          />

          <StatCard
            label="Total Bets"
            value={`₹${summary?.totalBetAmount?.toFixed(2) || "0.00"}`}
            icon={FiTrendingUp}
            color="blue"
            subtext={`${summary?.totalBetsCount || 0} rounds`}
          />

          <StatCard
            label="Total Wins"
            value={`₹${summary?.totalWinAmount?.toFixed(2) || "0.00"}`}
            icon={FiTrendingDown}
            color="orange"
            subtext={`${summary?.totalWinsCount || 0} wins`}
          />

          <StatCard
            label="Active Users"
            value={summary?.activeUsers || 0}
            icon={FiUsers}
            color="green"
            subtext="In selected period"
          />
        </SimpleGrid>

        {/* Charts */}
        <Box
          bg="white"
          p={6}
          borderRadius="lg"
          boxShadow="sm"
          mb={8}
          border="1px solid"
          borderColor="gray.100"
        >
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            Daily Volume
          </Text>

          <Box h="300px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />

                <Bar dataKey="betAmount" name="Bets" fill="#3182ce" />
                <Bar dataKey="winAmount" name="Wins" fill="#dd6b20" />
                <Bar dataKey="ggr" name="GGR" fill="#805ad5" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* Recent Wins */}
        <Box
          bg="white"
          borderRadius="lg"
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.100"
          overflow="hidden"
        >
          <Box p={4} borderBottom="1px solid" borderColor="gray.100">
            <Text fontSize="lg" fontWeight="bold">
              Recent High Wins
            </Text>
          </Box>

          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>User</Th>
                <Th>Game</Th>
                <Th>Amount</Th>
                <Th>Time</Th>
              </Tr>
            </Thead>

            <Tbody>
              {paginatedWins.map((win) => (
                <Tr key={win._id}>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Icon as={FiUsers} color="gray.400" />
                      <Text fontWeight="medium">
                        {win.user?.username || "Unknown"}
                      </Text>
                    </Flex>
                  </Td>

                  <Td>{win.description || "Casino Game"}</Td>

                  <Td fontWeight="bold" color="green.500">
                    +₹{win.amount?.toFixed(2)}
                  </Td>

                  <Td color="gray.500">
                    {new Date(win.createdAt).toLocaleString()}
                  </Td>
                </Tr>
              ))}

              {recentWins.length === 0 && (
                <Tr>
                  <Td colSpan={4} textAlign="center" py={6}>
                    No recent wins
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <Flex justify="flex-end" p={4} gap={2}>
              <Button
                size="sm"
                onClick={() => setPage(page - 1)}
                isDisabled={page === 1}
              >
                Prev
              </Button>

              {[...Array(totalPages)].map((_, i) => (
                <Button
                  key={i}
                  size="sm"
                  onClick={() => setPage(i + 1)}
                  bg={page === i + 1 ? "blue.500" : "white"}
                  color={page === i + 1 ? "white" : "gray.700"}
                  border="1px solid"
                  borderColor="gray.200"
                >
                  {i + 1}
                </Button>
              ))}

              <Button
                size="sm"
                onClick={() => setPage(page + 1)}
                isDisabled={page === totalPages}
              >
                Next
              </Button>
            </Flex>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CasinoAnalytics;
